// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workspaceBillsApi } from '@/api/workspace-bills-api'
import { useToast } from '@/hooks/use-toast'
import { getFilterScore } from '@/utils/monitor-analysis'
import type { WorkspaceBill, WorkspaceBillImpactScore, WorkspaceBillAction, WorkspaceBillsResponse } from '@/types/workspace-bill'
import type { APISession } from '@/types/session'
import { getJobByContext, JobContext } from '@umd-dit-ai-workspace/jobs-component'
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';

// Define sorting related types
export type SortField = 'impact' | 'bill_number' | 'title' | 'last_modified' | 'crossfile_bill_number' | null
export type SortDirection = 'asc' | 'desc'

interface WorkspaceBillsWorkflowReturn {
  workspace_bills: WorkspaceBill[]
  is_loading: boolean
  is_error: boolean
  error: string | null
  selected_bills: string[]
  min_impact_score: WorkspaceBillImpactScore
  selected_session: APISession
  set_selected_session: (session: APISession) => void
  set_min_impact_score: (score: WorkspaceBillImpactScore) => void
  handle_select_bill: (bill_id: string) => void
  handle_select_all: (bills: WorkspaceBill[]) => void
  process_bill_action: (bill_ids: string[], action: WorkspaceBillAction) => Promise<number>
  current_page: number
  page_size: number
  total_pages: number
  handle_page_change: (page: number) => void
  search_query: string
  set_search_query: (query: string) => void
  total_filtered_items: number
  processing_actions: Record<string, WorkspaceBillAction>
  sort_field: SortField
  sort_direction: SortDirection
  set_sorting: (field: SortField, direction: SortDirection) => void
  refresh_bills: () => void
}

export const useWorkspaceBillsWorkflow = (
  initial_min_impact_score: WorkspaceBillImpactScore = 0,
  initial_session: APISession
): WorkspaceBillsWorkflowReturn => {
  const { toast } = useToast()
  const query_client = useQueryClient()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const [min_impact_score, setMinImpactScore] = useState<WorkspaceBillImpactScore>(initial_min_impact_score)
  const [selected_bills, setSelectedBills] = useState<string[]>([])
  const [selected_session, setSelectedSession] = useState<APISession>(initial_session)
  const [current_page, setCurrentPage] = useState(1)
  const [search_query, setSearchQuery] = useState("")
  const [processing_actions, setProcessingActions] = useState<Record<string, WorkspaceBillAction>>({})
  const [sort_field, setSortField] = useState<SortField>('impact')
  const [sort_direction, setSortDirection] = useState<SortDirection>('desc')
  const page_size = 20 // Fixed page size for client-side pagination

  // Update internal min_impact_score when the debounced value changes
  useEffect(() => {
    setMinImpactScore(initial_min_impact_score)
  }, [initial_min_impact_score])

  useEffect(() => {
    setSelectedBills([])
  }, [min_impact_score, selected_session, selectedWorkspaceId])

  // Reset to first page when search query or impact score changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search_query, min_impact_score])

  // Function to handle sorting
  const set_sorting = useCallback((field: SortField, direction: SortDirection) => {
    setSortField(field)
    setSortDirection(direction)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Calculate impact value for sorting
  const get_impact_value = useCallback((bill: WorkspaceBill): number => {
    const impact = getFilterScore(bill.monitor_analysis)
    if (!impact) return -999 // No analysis should sort to the end
    
    // Create a numeric value for sorting that prioritizes type first, then score
    const type_value: Record<string, number> = { positive: 1000, neutral: 0, negative: -1000 }
    return type_value[impact.type] + impact.score
  }, [])

  // Sort bills based on current sort field and direction
  const sort_bills = useCallback((bills: WorkspaceBill[]): WorkspaceBill[] => {
    if (!sort_field) return bills

    return [...bills].sort((a, b) => {
      let comparison = 0

      if (sort_field === 'impact') {
        const value_a = get_impact_value(a)
        const value_b = get_impact_value(b)
        comparison = value_b - value_a // Default descending for impact
      } else if (sort_field === 'bill_number') {
        comparison = a.bill_number.localeCompare(b.bill_number)
      } else if (sort_field === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '')
      } else if (sort_field === 'last_modified') {
        comparison = a.last_modified.localeCompare(b.last_modified)
      } else if (sort_field === 'crossfile_bill_number') {
        // Handle null values for crossfile_bill_number
        if (!a.crossfile_bill_number && !b.crossfile_bill_number) {
          comparison = 0
        } else if (!a.crossfile_bill_number) {
          comparison = 1 // Sort nulls to the end
        } else if (!b.crossfile_bill_number) {
          comparison = -1 // Sort nulls to the end
        } else {
          comparison = a.crossfile_bill_number.localeCompare(b.crossfile_bill_number)
        }
      }

      // Apply sort direction
      return sort_direction === 'asc' ? comparison : -comparison
    })
  }, [sort_field, sort_direction, get_impact_value])

  // Simplified query using useQuery instead of useInfiniteQuery
  const bills_query = useQuery({
    queryKey: ['workspace-bills', selected_session, min_impact_score, selectedOrganizationId, selectedWorkspaceId],
    queryFn: async () => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        console.warn('[useWorkspaceBillsWorkflow] No workspace selected');
        throw new Error('No workspace selected');
      }

      console.log('[useWorkspaceBillsWorkflow] Fetching all workspace bills with params:', {
        session: selected_session,
        min_impact_score: min_impact_score
      });

      const response = await workspaceBillsApi.listWorkspaceBills({
        session: selected_session,
        min_impact_score: min_impact_score
      })

      console.log('[useWorkspaceBillsWorkflow] Fetched', response.items.length, 'workspace bills');
      return response
    },
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId,
    refetchOnWindowFocus: false,
    staleTime: 300000, // 5 minutes
    gcTime: 3600000, // 1 hour
  })

  // Get all bills from the query response
  const all_bills = useMemo(() => {
    const bills = bills_query.data?.items || []
    console.log(`[useWorkspaceBillsWorkflow] all_bills: ${bills.length} bills`)
    return bills
  }, [bills_query.data])

  // Filter bills based on search query
  const filtered_bills = useMemo(() => {
    if (!search_query.trim()) return all_bills;

    const query = search_query.trim().toUpperCase();
    return all_bills.filter(bill => {
      const bill_number = bill.bill_number || '';
      return bill_number.startsWith(query);
    });
  }, [all_bills, search_query])

  // Apply sorting to filtered bills
  const sorted_bills = useMemo(() => {
    return sort_bills(filtered_bills)
  }, [filtered_bills, sort_bills])

  // Get bills for the current page (client-side pagination)
  const get_current_page_bills = (): WorkspaceBill[] => {
    const start_idx = (current_page - 1) * page_size
    const end_idx = start_idx + page_size
    const result = sorted_bills.slice(start_idx, end_idx)

    console.log(`[useWorkspaceBillsWorkflow] get_current_page_bills: Returning ${result.length} bills for page ${current_page} (${start_idx}-${end_idx}) from ${sorted_bills.length} total bills`)

    return result
  }

  const total_items = sorted_bills.length
  const total_pages = Math.ceil(total_items / page_size)

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (current_page > total_pages && total_pages > 0) {
      setCurrentPage(total_pages);
    }
  }, [total_pages, current_page])

  const process_bill_action_mutation = useMutation({
    mutationFn: async ({ bill_numbers, action }: { bill_numbers: string[], action: WorkspaceBillAction }) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) throw new Error('No workspace selected')

      const results = await Promise.all(
        bill_numbers.map(bill_number =>
          workspaceBillsApi.processWorkspaceBillAction({
            session: selected_session,
            bill_number: bill_number,
            action: action
          })
        )
      )
      bill_numbers.forEach(bill_number => {
        setProcessingActions(prev => ({
          ...prev,
          [bill_number]: action
        }))
      })
      return {
        bill_numbers,
        action,
        job_ids: results.map(r => r.job_id)
      }
    },
    onSuccess: ({ bill_numbers, action, job_ids }) => {
      bill_numbers.forEach((bill_number, index) => {
        const job_id = job_ids[index]
        if (!job_id) return

        // Add a delay before starting to poll to give the job time to be created
        setTimeout(() => {
          const interval_id = setInterval(async () => {
            try {
              // Create job context with workspace information
              const job_context: JobContext = {
                org_id: selectedOrganizationId || undefined,
                ws_id: selectedWorkspaceId || undefined
              }
              console.log('Using context ' + JSON.stringify(job_context))

              // Use the workspace-aware job polling
              const job_status = await getJobByContext(job_context, job_id)

              // If job is complete or errored, stop polling
              if (job_status.status === 'COMPLETED' || job_status.status === 'ERROR') {
                clearInterval(interval_id)

                setProcessingActions(prev => {
                  const new_state = { ...prev }
                  delete new_state[bill_number]
                  return new_state
                })

                if (job_status.status === 'COMPLETED') {
                  console.log(`[useWorkspaceBillsWorkflow] Job completed for bill ${bill_number}`)
                  
                  try {
                    // Fetch the updated bill
                    const bill = await workspaceBillsApi.getWorkspaceBill(selected_session, bill_number)
                    console.log(`[useWorkspaceBillsWorkflow] Updated bill data:`, {
                      bill_number: bill.bill_number,
                      impact_score: bill.impact_score,
                      has_monitor_analysis: !!bill.monitor_analysis,
                      has_legislative_review: !!bill.legislative_review_s3_key
                    })

                    // Update the cache with the new bill data
                    const query_key = ['workspace-bills', selected_session, min_impact_score, selectedOrganizationId, selectedWorkspaceId]
                    
                    query_client.setQueryData(query_key, (old_data: WorkspaceBillsResponse) => {
                      if (!old_data?.items) return old_data
                      
                      const updated_data = {
                        ...old_data,
                        items: old_data.items.map((item: WorkspaceBill) => {
                          if (item.bill_number === bill_number) {
                            return bill
                          }
                          return item
                        })
                      }
                      
                      return updated_data
                    })
                    
                    console.log(`[useWorkspaceBillsWorkflow] Updated bill ${bill_number} in cache`)
                  } catch (error) {
                    console.error(`[useWorkspaceBillsWorkflow] Error updating bill cache for ${bill_number}:`, error)
                    // Fallback: invalidate cache to ensure consistency
                    query_client.invalidateQueries({
                      queryKey: ['workspace-bills', selected_session, min_impact_score, selectedOrganizationId, selectedWorkspaceId],
                      exact: true
                    })
                  }
                } else if (job_status.status === 'ERROR') {
                  toast({
                    title: "Processing Failed",
                    description: `Failed to process bill ${bill_number}`,
                    variant: "destructive"
                  })
                }
              }
            } catch (error) {
              console.error('Error polling for job status:', error)
            }
          }, 2000)

          // Clear interval after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(interval_id)
            setProcessingActions(prev => {
            const new_state = { ...prev }
            delete new_state[bill_number]
            return new_state
          })
        }, 300000)}, 3000); // Initial delay of 3 seconds before starting to poll
      })

      setSelectedBills([])
      toast({
        title: "Processing Started",
        description: `AI ${action} will be generated for ${bill_numbers.length} bill${bill_numbers.length === 1 ? '' : 's'}`
      })
    },
    onError: (error) => {
      console.error('Failed to process bill action:', error)
      toast({
        title: "Error",
        description: "Failed to start bill processing",
        variant: "destructive"
      })
    }
  })

  const handle_select_bill = useCallback((bill_id: string) => {
    setSelectedBills(prev => 
      prev.includes(bill_id) 
        ? prev.filter(id => id !== bill_id)
        : [...prev, bill_id]
    )
  }, [])

  const handle_select_all = useCallback((bills: WorkspaceBill[]) => {
    setSelectedBills(prev => 
      prev.length === bills.length 
        ? []
        : bills.map(b => b.bill_number)
    )
  }, [])

  const process_bill_action = async (bill_numbers: string[], action: WorkspaceBillAction) => {
    await process_bill_action_mutation.mutateAsync({ bill_numbers, action })
    return bill_numbers.length
  }

  const handle_page_change = (page: number) => {
    if (page >= 1 && page <= total_pages) {
      setCurrentPage(page);
    }
  }

  const refresh_bills = useCallback(() => {
    console.log('[useWorkspaceBillsWorkflow] Refreshing bills data')
    query_client.invalidateQueries({
      queryKey: ['workspace-bills', selected_session, min_impact_score, selectedOrganizationId, selectedWorkspaceId],
      exact: true
    })
  }, [query_client, selected_session, min_impact_score, selectedOrganizationId, selectedWorkspaceId])

  // Generate the final bills to return
  const bills_to_return = get_current_page_bills()

  // Debugging log to track what's being returned
  console.log(`[useWorkspaceBillsWorkflow] Returning hook data:`, {
    bills_count: bills_to_return.length,
    all_bills_count: all_bills.length,
    filtered_bills_count: filtered_bills.length,
    sorted_bills_count: sorted_bills.length,
    is_loading: bills_query.isPending || bills_query.isFetching,
    is_error: bills_query.isError,
    min_impact_score,
    total_items,
    current_page,
    total_pages,
    processing_actions,
    processing_actions_keys: Object.keys(processing_actions)
  })

  return {
    workspace_bills: bills_to_return,
    is_loading: bills_query.isPending || bills_query.isFetching,
    is_error: bills_query.isError,
    error: bills_query.isError ? 'Failed to load workspace bills' : null,
    selected_bills,
    min_impact_score,
    selected_session,
    set_selected_session: setSelectedSession,
    set_min_impact_score: setMinImpactScore,
    handle_select_bill,
    handle_select_all,
    process_bill_action,
    current_page,
    page_size,
    total_pages,
    handle_page_change,
    search_query,
    set_search_query: setSearchQuery,
    total_filtered_items: total_items,
    processing_actions,
    sort_field,
    sort_direction,
    set_sorting,
    refresh_bills
  }
}
